import cx from 'classnames';
import { useCallback, useId, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import TooltipWrapper from '@/components/base/TooltipWrapper/TooltipWrapper';
import Text from '@/storybook/components/Text/Text';
import AILogoGradientIcon from '@/storybook/icons/AILogoGradientIcon';
import AILogoIcon from '@/storybook/icons/AILogoIcon';

import styles from './AIButton.module.css';
import useAIButtonDrag from './hooks/useAIButtonDrag';
import useAIButtonHover from './hooks/useAIButtonHover';
import { getButtonStyle, getTooltipKey } from './AIButton.utils';
import {
  AI_BUTTON_DISABLED_REASON,
  AI_BUTTON_SIZE,
  AI_BUTTON_VARIANT,
  type AIButtonProps,
  type AIButtonSize,
  type AIButtonVariant,
  type DisabledReason,
} from './AIButton.types';

export { AI_BUTTON_DISABLED_REASON, AI_BUTTON_SIZE, AI_BUTTON_VARIANT };
export type { AIButtonProps, AIButtonSize, AIButtonVariant, DisabledReason };

export const TestIds = {
  aiButton: {
    root: 'ces-ai-button',
    icon: 'ces-ai-button-icon',
  },
};

const AIButton: React.FC<AIButtonProps> = ({
  onClick,
  disabled = false,
  disabledReason,
  variant = AI_BUTTON_VARIANT.purple,
  size = AI_BUTTON_SIZE.M,
  className,
  style,
}) => {
  const { t } = useTranslation();
  const tooltipId = useId();
  const aiButtonRef = useRef<HTMLButtonElement>(null);

  const { position, isDragging, consumeDragEndClick, dragHandlers } = useAIButtonDrag(aiButtonRef);
  const { isExpanded, isLingering, iconMargin, growDirection, handleMouseEnter, handleMouseLeave } = useAIButtonHover(
    isDragging,
    aiButtonRef,
  );

  const handleClick = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      if (isDragging || disabled || consumeDragEndClick()) return;
      onClick?.(event);
    },
    [consumeDragEndClick, isDragging, disabled, onClick],
  );

  const tooltipKey = getTooltipKey(disabledReason);
  const tooltipText = disabled && disabledReason ? String(t(tooltipKey)) : undefined;

  const showWhiteIcon = variant === AI_BUTTON_VARIANT.purple || disabled;
  const Icon = showWhiteIcon ? AILogoIcon : AILogoGradientIcon;

  const buttonStyle = getButtonStyle(position);

  return (
    <>
      <button
        ref={aiButtonRef}
        type="button"
        tabIndex={disabled ? -1 : 0}
        className={cx(styles.aiButton, { [styles.whiteVariant]: variant === AI_BUTTON_VARIANT.white }, className)}
        style={{ ...buttonStyle, ...style }}
        data-testid={TestIds.aiButton.root}
        data-size={size}
        data-grow-direction={growDirection}
        data-expanded={isExpanded}
        data-disabled={disabled}
        data-dragging={isDragging}
        data-lingering={isLingering}
        data-icon-margin={iconMargin}
        aria-label={t('components.aiButton.ariaLabel')}
        aria-disabled={disabled || undefined}
        aria-describedby={tooltipText ? tooltipId : undefined}
        data-tooltip-id={tooltipText ? tooltipId : undefined}
        data-tooltip-html={tooltipText}
        onClick={handleClick}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        {...dragHandlers}
      >
        <Icon className={styles.icon} data-testid={TestIds.aiButton.icon} />
        <Text as="span" className={styles.label}>
          {t('components.aiButton.label')}
        </Text>
      </button>

      {tooltipText && <TooltipWrapper id={tooltipId} place="top" className={styles.tooltip} />}
    </>
  );
};

export default AIButton;
